import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { toISODate, formatDateIt } from '../../utils/vacation';
import {
  Store,
  Plane,
  CalendarDays
} from 'lucide-react';

export default function DashboardSettings({ role }) {
  const { profile } = useAuth();
  const [storefrontDisabled, setStorefrontDisabled] = useState(false);
  const [sfBusy, setSfBusy] = useState(false);
  // Ferie
  const [vacRange, setVacRange] = useState(undefined); // { from, to }
  const [vacMessage, setVacMessage] = useState('');
  const [vacActive, setVacActive] = useState(false);
  const [vacStart, setVacStart] = useState(null);
  const [vacEnd, setVacEnd] = useState(null);
  const [vacBusy, setVacBusy] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const API_SF = process.env.REACT_APP_BACKEND_URL;
  const isCrafter = role === 'crafter' || profile?.is_crafter_enabled;

  useEffect(() => {
    if (!isCrafter) return;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const res = await fetch(`${API_SF}/api/account/status`, {
          headers: { Authorization: `Bearer ${data?.session?.access_token}` },
        });
        if (res.ok) {
          const d = await res.json();
          setStorefrontDisabled(!!d.storefront_disabled);
          setVacActive(!!d.vacation_active);
          setVacStart(d.vacation_start || null);
          setVacEnd(d.vacation_end || null);
          setVacMessage(d.vacation_message || '');
          if (d.vacation_start && d.vacation_end) {
            setVacRange({ from: new Date(`${d.vacation_start}T00:00:00`), to: new Date(`${d.vacation_end}T00:00:00`) });
          }
        }
      } catch { /* noop */ }
    })();
  }, [isCrafter, API_SF]);

  const toggleStorefront = async (makeActive) => {
    setSfBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${API_SF}/api/account/storefront`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data?.session?.access_token}` },
        body: JSON.stringify({ disabled: !makeActive }),
      });
      if (!res.ok) throw new Error('Operazione non riuscita');
      setStorefrontDisabled(!makeActive);
      toast.success(makeActive ? 'Vetrina riattivata' : 'Vetrina disattivata');
    } catch (e) { toast.error(e.message); } finally { setSfBusy(false); }
  };

  const saveVacation = async () => {
    if (!vacRange?.from || !vacRange?.to) {
      toast.error('Seleziona un periodo di ferie (inizio e fine)');
      return;
    }
    setVacBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const start = toISODate(vacRange.from);
      const end = toISODate(vacRange.to);
      const res = await fetch(`${API_SF}/api/account/vacation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data?.session?.access_token}` },
        body: JSON.stringify({ start, end, message: vacMessage || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Operazione non riuscita');
      }
      setVacStart(start);
      setVacEnd(end);
      const today = toISODate(new Date());
      setVacActive(start <= today && today <= end);
      toast.success('Periodo di ferie salvato');
    } catch (e) { toast.error(e.message); } finally { setVacBusy(false); }
  };

  const endVacation = async () => {
    setVacBusy(true);
    try {
      const { data } = await supabase.auth.getSession();
      const res = await fetch(`${API_SF}/api/account/vacation/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${data?.session?.access_token}` },
      });
      if (!res.ok) throw new Error('Operazione non riuscita');
      setVacRange(undefined);
      setVacStart(null);
      setVacEnd(null);
      setVacMessage('');
      setVacActive(false);
      toast.success('Chiusura per ferie terminata');
    } catch (e) { toast.error(e.message); } finally { setVacBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-settings">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Impostazioni</h2>
        <p className="text-slate-500 mb-6">Personalizza la tua esperienza sulla dashboard</p>
      </motion.div>

      {/* Chiusura per ferie (solo artigiani) */}
      {isCrafter && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="p-6" data-testid="vacation-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Plane className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Chiusura per ferie</h3>
              <p className="text-sm text-slate-500">Imposta un periodo di assenza: i prodotti restano visibili ma non acquistabili fino al rientro.</p>
            </div>
          </div>

          {vacActive && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="vacation-active-note">
              Sei attualmente <strong>in ferie</strong> fino al {formatDateIt(vacEnd)}. I tuoi prodotti non sono acquistabili.
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label className="font-medium mb-2 block">Periodo di assenza</Label>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="vacation-range-trigger"
                  >
                    <CalendarDays className="w-4 h-4 mr-2 text-amber-600" />
                    {vacRange?.from && vacRange?.to
                      ? `${formatDateIt(vacRange.from)} → ${formatDateIt(vacRange.to)}`
                      : 'Seleziona date di inizio e fine'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={vacRange}
                    onSelect={setVacRange}
                    numberOfMonths={2}
                    disabled={{ before: new Date() }}
                    data-testid="vacation-calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="font-medium mb-2 block">Messaggio per i clienti (facoltativo)</Label>
              <Textarea
                value={vacMessage}
                onChange={(e) => setVacMessage(e.target.value)}
                maxLength={280}
                rows={2}
                placeholder="Es. Torno il 2 settembre, grazie per la pazienza!"
                data-testid="vacation-message-input"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <Button
                onClick={saveVacation}
                disabled={vacBusy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="vacation-save-btn"
              >
                {vacStart ? 'Aggiorna ferie' : 'Attiva ferie'}
              </Button>
              {vacStart && (
                <Button
                  onClick={endVacation}
                  disabled={vacBusy}
                  variant="outline"
                  data-testid="vacation-end-btn"
                >
                  Termina ferie ora
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400">
              Al termine del periodo la vetrina torna automaticamente acquistabile. Puoi rientrare in anticipo con "Termina ferie ora".
            </p>
          </div>
        </Card>
      </motion.div>
      )}

      {/* Sospendi attività (solo artigiani) */}
      {isCrafter && (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Store className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Sospendi attività</h3>
              <p className="text-sm text-slate-500">Attiva o disattiva la tua vetrina pubblica</p>
            </div>
          </div>

          <div className="flex items-center justify-between border rounded-xl p-4">
            <div>
              <Label className="font-medium">Vetrina {storefrontDisabled ? 'disattivata' : 'attiva'}</Label>
              <p className="text-sm text-slate-500 mt-1">
                {storefrontDisabled
                  ? 'La tua vetrina e i tuoi prodotti NON sono visibili al pubblico. Riattiva quando vuoi.'
                  : 'La tua vetrina e i tuoi prodotti sono visibili al pubblico.'}
              </p>
            </div>
            <Switch
              checked={!storefrontDisabled}
              disabled={sfBusy}
              onCheckedChange={(v) => toggleStorefront(v)}
              data-testid="storefront-toggle"
            />
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Disattivare la vetrina nasconde profilo artigiano, prodotti e recensioni, ma non elimina nulla: puoi ripristinare in qualsiasi momento con questo stesso interruttore.
          </p>
        </Card>
      </motion.div>
      )}
    </div>
  );
}
